import { useEffect, useMemo, useState } from 'react';
import { Outlet, Navigate, useNavigate } from 'react-router-dom';
import { CUSTOMER_ROUTES, can } from '@cvsi/shared';
import { TopNavigation } from './TopNavigation';
import { useTenant } from '../lib/tenantContext';

/** Global page and workflow search available throughout the authenticated portal. */
function CommandSearch() {
  const { context } = useTenant();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const routes = useMemo(() => {
    if (!context) return [];
    return CUSTOMER_ROUTES
      .filter((r) => r.scope == null || can(context.role, r.scope))
      .map((r) => ({
        ...r,
        keywords: `${r.label} ${r.path.replace('/', '').replace(/-/g, ' ')}`.toLowerCase(),
      }));
  }, [context]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return routes.slice(0, 8);
    return routes.filter((r) => r.keywords.includes(q)).slice(0, 8);
  }, [query, routes]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const typing = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.tagName === 'SELECT';
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (!typing && e.key === '/') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function go(path: string) {
    setOpen(false);
    setQuery('');
    navigate(path);
  }

  return (
    <div className="command-search">
      <button className="secondary small command-trigger" onClick={() => setOpen(true)}>Search</button>
      {open && (
        <div className="command-popover" role="dialog" aria-label="Portal search">
          <div className="command-box">
            <input
              autoFocus
              aria-label="Search pages and workflows"
              placeholder="Search pages and workflows"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="command-results">
              {matches.length === 0 ? (
                <div className="command-empty">No matching portal page.</div>
              ) : matches.map((r) => (
                <button key={r.path} className="command-result" onClick={() => go(r.path)}>
                  <span>{r.label}</span>
                  <span className="muted">{r.path}</span>
                </button>
              ))}
            </div>
            <div className="command-foot">Page and workflow search. Record search will appear after the backend search index is available.</div>
          </div>
          <button className="command-backdrop" aria-label="Close search" onClick={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

/** Authenticated layout with a responsive, role-aware top navigation menu. */
export function AppShell() {
  const { session } = useTenant();
  if (!session) return <Navigate to="/login" replace />;
  return (
    <div className="shell">
      <TopNavigation />
      <main className="main">
        <div className="search-row">
          <CommandSearch />
        </div>
        <div className="portal-page">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
